import { useCallback, useEffect, useRef, useState } from 'react';

import { useTranscribeAudioMutation } from '../graphql/AudioTranscription.generated';

const MAX_RECORDING_DURATION_MS = 120_000;
const MAX_RECORDING_BYTES = 10 * 1024 * 1024;
const WAVEFORM_BAR_COUNT = 16;

const SUPPORTED_RECORDING_TYPES = [
  { extension: 'webm', mimeType: 'audio/webm' },
  { extension: 'mp4', mimeType: 'audio/mp4' },
] as const;

export type DictationState =
  | 'idle'
  | 'requestingPermission'
  | 'recording'
  | 'transcribing';

interface UseHouseholdChatDictationOptions {
  onError: (message: string) => void;
  onTranscript: (transcript: string) => void;
  reducedMotion: boolean;
}

function getRecordingType() {
  return SUPPORTED_RECORDING_TYPES.find(({ mimeType }) =>
    MediaRecorder.isTypeSupported(mimeType)
  );
}

export function useHouseholdChatDictation({
  onError,
  onTranscript,
  reducedMotion,
}: UseHouseholdChatDictationOptions) {
  const [transcribeAudio] = useTranscribeAudioMutation();
  const [state, setState] = useState<DictationState>('idle');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [waveform, setWaveform] = useState<number[]>(
    Array(WAVEFORM_BAR_COUNT).fill(0.2)
  );
  const audioContextRef = useRef<AudioContext | undefined>(undefined);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const canceledRef = useRef(false);
  const chunksRef = useRef<Blob[]>([]);
  const durationIntervalRef = useRef<
    ReturnType<typeof setInterval> | undefined
  >(undefined);
  const maxDurationTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const mediaRecorderRef = useRef<MediaRecorder | undefined>(undefined);
  const mountedRef = useRef(true);
  const permissionRequestRef = useRef(0);
  const streamRef = useRef<MediaStream | undefined>(undefined);

  const stopMediaResources = useCallback(() => {
    if (animationFrameRef.current !== undefined) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
    if (durationIntervalRef.current !== undefined) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = undefined;
    }
    if (maxDurationTimeoutRef.current !== undefined) {
      clearTimeout(maxDurationTimeoutRef.current);
      maxDurationTimeoutRef.current = undefined;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = undefined;
    if (audioContextRef.current) {
      void audioContextRef.current.close();
      audioContextRef.current = undefined;
    }
  }, []);

  const uploadRecording = useCallback(
    async (blob: Blob, extension: string) => {
      if (blob.size > MAX_RECORDING_BYTES) {
        onError('That recording is too large. Try a shorter dictation.');
        setState('idle');
        return;
      }

      setState('transcribing');
      const upload = new File([blob], `dictation.${extension}`, {
        type: blob.type,
      });

      try {
        const result = await transcribeAudio({
          variables: { upload },
          context: { hasUpload: true },
        });
        const transcript = result.data?.transcribeAudio;
        if (typeof transcript !== 'string') {
          throw new Error('Transcription request failed');
        }
        if (mountedRef.current) {
          onTranscript(transcript.trim());
        }
      } catch {
        if (mountedRef.current) {
          onError('We could not transcribe that recording. Please try again.');
        }
      } finally {
        if (mountedRef.current) {
          setState('idle');
        }
      }
    },
    [onError, onTranscript, transcribeAudio]
  );

  const stop = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state === 'recording') {
      recorder.stop();
    }
  }, []);

  const cancel = useCallback(() => {
    canceledRef.current = true;
    permissionRequestRef.current += 1;
    const recorder = mediaRecorderRef.current;
    if (recorder?.state === 'recording') {
      recorder.stop();
    } else {
      stopMediaResources();
      setState('idle');
    }
  }, [stopMediaResources]);

  const startWaveform = useCallback(
    (stream: MediaStream) => {
      if (reducedMotion) {
        return;
      }
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 64;
      audioContext.createMediaStreamSource(stream).connect(analyser);
      audioContextRef.current = audioContext;
      const levels = new Uint8Array(analyser.frequencyBinCount);

      const updateWaveform = () => {
        analyser.getByteFrequencyData(levels);
        setWaveform(
          Array.from({ length: WAVEFORM_BAR_COUNT }, (_, index) =>
            Math.max(0.12, (levels[index * 2] ?? 0) / 255)
          )
        );
        animationFrameRef.current = requestAnimationFrame(updateWaveform);
      };
      updateWaveform();
    },
    [reducedMotion]
  );

  const start = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      onError('Audio recording is not supported by this browser.');
      return;
    }
    const recordingType = getRecordingType();
    if (!recordingType) {
      onError('Audio recording is not supported by this browser.');
      return;
    }

    const requestID = permissionRequestRef.current + 1;
    permissionRequestRef.current = requestID;
    canceledRef.current = false;
    setState('requestingPermission');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!mountedRef.current || permissionRequestRef.current !== requestID) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;
      chunksRef.current = [];
      const recorder = new MediaRecorder(stream, {
        mimeType: recordingType.mimeType,
      });
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = ({ data }) => {
        if (data.size > 0) {
          chunksRef.current.push(data);
        }
      };
      recorder.onerror = () => {
        canceledRef.current = true;
        stopMediaResources();
        if (mountedRef.current) {
          onError('Recording stopped unexpectedly. Please try again.');
          setState('idle');
        }
      };
      recorder.onstop = () => {
        stopMediaResources();
        mediaRecorderRef.current = undefined;
        if (canceledRef.current || !mountedRef.current) {
          chunksRef.current = [];
          if (mountedRef.current) {
            setState('idle');
          }
          return;
        }
        const blob = new Blob(chunksRef.current, {
          type: recordingType.mimeType,
        });
        chunksRef.current = [];
        void uploadRecording(blob, recordingType.extension);
      };

      setElapsedSeconds(0);
      const startedAt = Date.now();
      durationIntervalRef.current = setInterval(() => {
        setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
      }, 250);
      maxDurationTimeoutRef.current = setTimeout(
        stop,
        MAX_RECORDING_DURATION_MS
      );
      startWaveform(stream);
      recorder.start();
      setState('recording');
    } catch {
      stopMediaResources();
      if (mountedRef.current) {
        onError('Microphone access is required to dictate a prompt.');
        setState('idle');
      }
    }
  }, [onError, startWaveform, stop, stopMediaResources, uploadRecording]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      canceledRef.current = true;
      permissionRequestRef.current += 1;
      const recorder = mediaRecorderRef.current;
      if (recorder?.state === 'recording') {
        recorder.stop();
      }
      stopMediaResources();
    };
  }, [stopMediaResources]);

  return {
    cancel,
    elapsedSeconds,
    start,
    state,
    stop,
    waveform,
  };
}
